import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Save, UserCog, Wallet, Building2, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const GRADES = ["Initial I", "Initial II", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Diploma"];
const ROLE_LABELS = { administrator: "Administrator", keuangan: "Keuangan", customer_service: "Customer Service" };
const ROLE_STYLES = {
  administrator: "bg-primary/10 text-primary hover:bg-primary/10",
  keuangan: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  customer_service: "bg-amber-100 text-amber-700 hover:bg-amber-100",
};
const EMPTY_USER = { name: "", email: "", password: "", role: "customer_service" };

function GradeFeesTab() {
  const [classes, setClasses] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/settings/class-fees").then((res) => setClasses(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setFee = (classId, grade, value) => {
    setClasses((prev) => prev.map((c) => c.id === classId
      ? { ...c, grade_fees: { ...c.grade_fees, [grade]: value } } : c));
  };

  const save = async (cls) => {
    setSaving(cls.id);
    try {
      await api.put(`/settings/class-fees/${cls.id}`, {
        grade_fees: Object.fromEntries(GRADES.map((g) => [g, Number(cls.grade_fees[g]) || 0])),
      });
      toast.success(`Tarif ${cls.name} tersimpan`);
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan tarif"));
    } finally {
      setSaving(null);
    }
  };

  if (!classes) {
    return <p className="py-16 text-center text-muted-foreground" data-testid="fees-loading">Memuat tarif...</p>;
  }

  if (classes.length === 0) {
    return (
      <Card className="border-border shadow-sm">
        <CardContent className="py-14 text-center text-muted-foreground" data-testid="fees-empty">
          Belum ada kelas. Tambahkan kelas terlebih dahulu di menu Kelas & Instrumen.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Tarif SPP diatur per kelas dan per grade — misalnya Gitar Akustik Grade 1 dapat berbeda dengan Gitar Akustik Grade 5.
        Tarif ini menjadi nilai default saat mencatat pembayaran SPP sesuai kelas & grade siswa.
      </p>
      {classes.map((cls) => (
        <Card key={cls.id} className="border-border shadow-sm" data-testid={`class-fees-card-${cls.id}`}>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wallet className="h-5 w-5 text-primary" /> {cls.name}
              <span className="text-sm font-normal text-muted-foreground">({cls.instrument})</span>
            </CardTitle>
            <Button onClick={() => save(cls)} disabled={saving === cls.id} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`save-fees-${cls.id}`}>
              {saving === cls.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {GRADES.map((g) => (
                <div key={g} className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide">{g}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">Rp</span>
                    <Input
                      type="number"
                      min="0"
                      className="pl-9 text-right"
                      value={cls.grade_fees[g] ?? ""}
                      onChange={(e) => setFee(cls.id, g, e.target.value)}
                      data-testid={`fee-input-${cls.id}-${g.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((res) => setUsers(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_USER); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { ...form, password: form.password || null });
        toast.success("Pengguna diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("Pengguna baru ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan pengguna"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus pengguna "${u.name}" (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus pengguna"));
    }
  };

  return (
    <Card className="border-border shadow-sm" data-testid="users-card">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserCog className="h-5 w-5 text-primary" /> Manajemen Pengguna
        </CardTitle>
        <Button onClick={openAdd} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-user-button">
          <Plus className="h-4 w-4" /> Tambah Pengguna
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Badge className={ROLE_STYLES[u.role] || ""} data-testid={`user-role-${u.id}`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil className="h-4 w-4 text-primary" />
                  </Button>
                  {u.id !== me?.id && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nama *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="space-y-2">
              <Label>{editing ? "Kata Sandi Baru (kosongkan jika tidak diubah)" : "Kata Sandi * (min. 8 karakter)"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrator">Administrator — akses penuh</SelectItem>
                  <SelectItem value="keuangan">Keuangan — SPP, pengeluaran & laporan</SelectItem>
                  <SelectItem value="customer_service">Customer Service — data siswa & kelas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="user-cancel-button">Batal</Button>
            <Button
              onClick={save}
              disabled={saving || !form.name || !form.email || (!editing && form.password.length < 8)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="user-save-button"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ProfileTab() {
  const { refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/tenant/profile")
      .then((res) => {
        setForm({ name: res.data.name || "", address: res.data.address || "", phone: res.data.phone || "" });
        setLogoUrl(res.data.logo_url || "");
      })
      .catch(() => {});
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Ukuran logo maksimal 2 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await api.post("/tenant/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
      });
      setLogoUrl(data.logo_url);
      await refresh();
      toast.success("Logo sekolah diperbarui");
    } catch (err) {
      toast.error(apiError(err, "Gagal mengunggah logo"));
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/tenant/logo");
      setLogoUrl("");
      await refresh();
      toast.success("Logo dihapus");
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus logo"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/tenant/profile", form);
      toast.success("Profil sekolah tersimpan");
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan profil"));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <p className="py-16 text-center text-muted-foreground" data-testid="profile-loading">Memuat profil...</p>;
  }

  return (
    <Card className="max-w-xl border-border shadow-sm" data-testid="profile-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5 text-primary" /> Profil Sekolah
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-5 text-sm text-muted-foreground">
          Nama, alamat, telepon, dan logo ini tampil pada kwitansi dan laporan yang dicetak.
        </p>
        <div className="mb-6 space-y-3">
          <Label>Logo Sekolah</Label>
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo sekolah" className="h-16 w-16 rounded-xl border border-border bg-white object-contain" data-testid="logo-preview" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-dashed border-border text-muted-foreground" data-testid="logo-preview-empty">
                <ImageIcon className="h-6 w-6" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2" data-testid="logo-upload-button">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? `Mengunggah ${progress}%` : logoUrl ? "Ganti Logo" : "Unggah Logo"}
              </Button>
              {logoUrl && (
                <Button type="button" variant="ghost" onClick={removeLogo} className="gap-2 text-destructive hover:text-destructive" data-testid="logo-delete-button">
                  <Trash2 className="h-4 w-4" /> Hapus Logo
                </Button>
              )}
              <p className="text-xs text-muted-foreground">JPG/PNG/WebP, maksimal 2 MB</p>
            </div>
          </div>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Nama Sekolah</Label>
            <Input id="profile-name" data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-address">Alamat</Label>
            <Input id="profile-address" data-testid="profile-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Alamat lengkap sekolah" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-phone">Telepon / WhatsApp</Label>
            <Input id="profile-phone" data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xx-xxxx-xxxx" />
          </div>
          <Button type="submit" disabled={saving} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="profile-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Profil
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();

  if (user?.role !== "administrator") {
    return (
      <div className="py-20 text-center text-muted-foreground" data-testid="settings-forbidden">
        Halaman ini hanya dapat diakses oleh Administrator.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Pengaturan</h1>
        <p className="mt-1 text-sm text-muted-foreground">Profil sekolah, tarif SPP per grade, dan manajemen pengguna.</p>
      </div>
      <Tabs defaultValue="profil">
        <TabsList>
          <TabsTrigger value="profil" data-testid="tab-profil">Profil Sekolah</TabsTrigger>
          <TabsTrigger value="tarif" data-testid="tab-tarif">Tarif SPP per Grade</TabsTrigger>
          <TabsTrigger value="pengguna" data-testid="tab-pengguna">Pengguna</TabsTrigger>
        </TabsList>
        <TabsContent value="profil" className="mt-6">
          <ProfileTab />
        </TabsContent>
        <TabsContent value="tarif" className="mt-6">
          <GradeFeesTab />
        </TabsContent>
        <TabsContent value="pengguna" className="mt-6">
          <UsersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
